import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import qs from "qs";
import TableBody from "@material-ui/core/TableBody";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { snakeCase } from "lodash";
import { DocumentNode } from "graphql";

import { useQueryCustom } from "hooks";
import { DataHeader, DataRow, ResultNotFound, Search, StyledGrid } from "components/simple";
import { HeadingObject } from "types";
import { StyledTable } from "./styled";
import PageFinder from "../PageFinder";

interface DataTableProps {
  gqlQuery: DocumentNode;
  queryName: string;
  forceRefetch?: boolean;
  setForceRefetch?: React.Dispatch<boolean>;
  headData: HeadingObject[];
  rowProcessor(a: any): JSX.Element[];
  hideSearch?: boolean;
  params?: any;
}

const DataTable: React.FC<DataTableProps> = ({
  gqlQuery,
  queryName,
  forceRefetch,
  setForceRefetch,
  headData,
  rowProcessor,
  hideSearch = false,
  params
}: DataTableProps) => {
  const history = useHistory();

  const queryParams = qs.parse(history.location.search, {
    ignoreQueryPrefix: true
  });

  const [activeHeadData, setActiveHeadData] = useState<HeadingObject[]>(headData);
  const [pageNum, setPageNum] = useState(
    typeof queryParams.page === "string" ? parseInt(queryParams.page, 10) : 1
  );
  const [pageLimit] = useState(25);
  const [filterColumn, setFilterColumn] = useState("");
  const [filterValue, setFilterValue] = useState("");
  const [sortValue, setSortValue] = useState("");
  const [searchColumns] = useState<string[]>(
    headData.filter(d => d.menu && d.menu.searchable).map(d => (d.menu && d.menu.dbColumn) || "")
  );
  const [searchValue, setSearchValue] = useState("");
  const [order, setOrder] = useState<"asc" | "desc" | null>(null);

  const { data, refetch, error } = useQueryCustom(gqlQuery, params ? { variables: params } : null);

  useEffect(() => {
    if (forceRefetch && setForceRefetch) {
      refetch();
      setForceRefetch(false);
    }
  }, [forceRefetch]); // eslint-disable-line

  // update status base on the query params
  useEffect(() => {
    if (queryParams) {
      const statusValue = queryParams.status;
      const searchKeyValue = queryParams.search;

      if (typeof statusValue === "string" && filterValue !== statusValue) {
        setFilterColumn("status");
        setFilterValue(statusValue);
      }
      if (typeof searchKeyValue === "string" && searchValue !== searchKeyValue) {
        setSearchValue(searchKeyValue);
      }
    }
  }, [filterValue, history.location.search, queryParams]);

  // update page base on the query params
  useEffect(() => {
    if (queryParams) {
      const pageValue = queryParams.page;

      if (typeof pageValue === "string") {
        const pageNumberFromQuery = parseInt(pageValue, 10);

        if (pageNumberFromQuery !== pageNum) {
          setPageNum(pageNumberFromQuery);
        }
      }
    }
  }, [history.location.search, pageNum, queryParams]);

  // update the graphQL pagination and filter values
  useEffect(() => {
    const variables: Record<string, any> = {};
    variables.filters = filterValue !== "" ? [{ filterColumn, filterValue }] : undefined;
    variables.sort = sortValue !== "" && order !== null ? { column: sortValue, order } : undefined;
    variables.searches =
      searchValue !== "" ? { columns: searchColumns, search: searchValue } : undefined;
    variables.page = pageNum;
    variables.limit = pageLimit;

    refetch({ ...variables });
  }, [filterColumn, filterValue, order, sortValue, searchValue, pageNum, pageLimit]); // eslint-disable-line

  useEffect(() => {
    setActiveHeadData(
      activeHeadData.map(d => {
        const newD = d;

        if (!newD.menu) return newD;
        if (
          newD.menu &&
          newD.menu.filter &&
          newD.menu.filter.find(f => snakeCase(f) === filterValue) !== undefined
        ) {
          newD.menu.filtered = true;
        } else {
          newD.menu.filtered = false;
        }
        if (newD.menu && newD.menu.dbColumn === sortValue) {
          newD.menu.ordered = order;
        } else {
          newD.menu.ordered = null;
        }
        return newD;
      })
    );
  }, [filterValue, order, sortValue]); // eslint-disable-line

  useEffect(() => setActiveHeadData(headData), [headData]);

  if (error || !data || !data[queryName] || !data[queryName].data) return <></>;

  const rowsData: any[] = data[queryName].data;

  const resetConstraints = (): void => {
    setFilterValue("");
    setSortValue("");
    setSearchValue("");
    setPageNum(1);
    history.push("");
  };

  const updateSearch = (newSearch: string): void => {
    if (filterColumn) history.replace(`?${filterColumn}=${filterValue}&page=1`);
    else history.replace("?page=1");
    setSearchValue(newSearch);
    setPageNum(1);
    const newQueryParams = {
      ...queryParams,
      search: newSearch,
      page: 1
    };
    history.push("?" + qs.stringify(newQueryParams));
  };

  const updateFilter = (newColumn: string, newFilter: string): void => {
    const newQueryParams = {
      ...queryParams,
      [newColumn]: newFilter,
      page: 1
    };
    history.push("?" + qs.stringify(newQueryParams));
    setFilterColumn(newColumn);
    setFilterValue(newFilter);
    setPageNum(1);
  };

  const updateSort = (newSort: string, newOrder?: "asc" | "desc"): void => {
    setSortValue(newSort);
    setOrder(newOrder || null);
  };

  return (
    <>
      {!hideSearch && (
        <Search setSearchValue={updateSearch} placeholder={"Search"} defaultValue={searchValue} />
      )}
      {rowsData.length === 0 ? (
        <ResultNotFound callback={resetConstraints} />
      ) : (
        <StyledGrid>
          <StyledTable>
            <TableHead>
              <TableRow>
                {activeHeadData
                  .filter(x => !x.menu?.hidden)
                  .map((data, index) => {
                    return (
                      <DataHeader
                        key={index}
                        align={index === headData.length - 1 ? "right" : "left"}
                        headData={data}
                        setFilterValue={updateFilter}
                        setSortValue={updateSort}
                      />
                    );
                  })}
              </TableRow>
            </TableHead>
            <TableBody>
              {rowsData.map((data, index) => (
                <DataRow key={index} rowData={rowProcessor(data)} />
              ))}
            </TableBody>
          </StyledTable>
        </StyledGrid>
      )}
      <PageFinder
        activePage={pageNum}
        setActivePage={setPageNum}
        totalPages={data[queryName].pageInfo.pageTotal}
      />
    </>
  );
};

export default DataTable;
